import { useCallback, useEffect, useRef, useState } from "react";

import {
  type BackendFilters,
  convertFilterModelToBackendFilters,
  validateBackendFilters,
} from "../../utils/backendFilters";
import { loadTableState } from "./persistence";
import type { FilterModel } from "./types";

interface ServerFilterOptions {
  persistKey?: string;
  mapField?: (field: string) => string;
  onFiltersChange?: (filters: BackendFilters) => void;
}

// Restores the saved filter before first render; sends each distinct valid change.
export default function useServerFilterModel({
  persistKey,
  mapField,
  onFiltersChange,
}: ServerFilterOptions) {
  const [filterModel, setFilterModel] = useState<FilterModel>(
    () =>
      (persistKey && loadTableState(persistKey)?.filter?.filterModel) || {
        items: [],
      },
  );
  const lastSent = useRef<string>();

  const onFilterModelChange = useCallback(
    (model: FilterModel) => {
      setFilterModel(model);
      const filters = convertFilterModelToBackendFilters(model, mapField);
      if (!onFiltersChange || validateBackendFilters(filters).length > 0) {
        return;
      }
      const serialized = JSON.stringify(filters);
      if (serialized === lastSent.current) return;
      lastSent.current = serialized;
      onFiltersChange(filters);
    },
    [mapField, onFiltersChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: send the restored filter once, on mount
  useEffect(() => {
    if (filterModel.items.length > 0) onFilterModelChange(filterModel);
  }, []);

  return { filterModel, onFilterModelChange };
}
